<template>
  <div>
    <div ref="triggerMap" :style="triggerMapStyle"/>

    <modal :id="modalId" :title="modalTitle" @shown="initBigMap">
      <div v-if="hasRadius" class="text-center alert alert-info">
        Service Area: {{radiusMiles}} miles
      </div>

      <div class="bigMap" ref="bigMap"/>
      <template #footer>
        <button type="button" class="btn btn-white" @click="hideModal">Close</button>
      </template>
    </modal>
  </div>
</template>

<script>
  /**
   * This shows small static google map and a button to open big map in modal dialog.
   */
  import HasModal from "./mixins/HasModal";
  import gmapsInit from './../utils/gmaps'

  export default {
    mixins: [HasModal],

    /**
     * Input properties.
     */
    props: {

      // Google Maps Api key.
      apiKey: {
        type: String,
        default: '',
      },

      // Google Maps Api base url.
      apiUrl: {
        type: String,
        default: 'https://maps.googleapis.com/maps/api/',
      },

      // Map center latitude.
      latitude: {
        type: String,
        default: '',
      },

      // Map center longitude.
      longitude: {
        type: String,
        default: '',
      },

      // Modal title.
      modalTitle: {
        type: String,
        default: '',
      },

      // Show radius (marker and circle).
      showRadius: {
        type: Boolean,
        default: false,
      },

      // Show radius (marker and circle).
      radiusMiles: {
        type: Number,
        default: 0,
      },

      triggerMapWidth: {
        type: String,
        default: '100%',
      },

      triggerMapHeight: {
        type: String,
        default: '15vh',
      },

      // Custom id for modal component.
      modalId: {
        type: String,
        default: 'gmapModal'
      },
    },

    /**
     * Variables (models).
     */
    data () {
      return {
      }
    },

    /**
     * Functions (methods).
     */
    methods: {

      /**
       * Init trigger map.
       */
      async initTriggerMap(position) {
        const options = {
          center: position,
          zoom: 8,
          mapTypeId: google.maps.MapTypeId.ROADMAP,
          disableDefaultUI: true,
          zoomControl: false,
          mapTypeControl: false,
          scaleControl: false,
          streetViewControl: false,
          rotateControl: false,
          fullscreenControl: false,
          draggableCursor: 'pointer',
          mapId: 'G_MAP',
        };
        const map = new google.maps.Map(this.$refs.triggerMap, options);
        const {AdvancedMarkerElement} = await google.maps.importLibrary("marker");

        // Add marker.
        const marker = new AdvancedMarkerElement({
          position: position,
          map: map
        });

        // Add click event for trigger map.
        let _this = this;
        map.addListener('click', function() {
          _this.showModal();
        });
      },

      /**
       * Init big map.
       */
      async initBigMap() {
        const position = new google.maps.LatLng(this.latitude, this.longitude);
        const {AdvancedMarkerElement} = await google.maps.importLibrary("marker");

        const options = {
          center: position,
          zoom: 8,
          mapTypeId: google.maps.MapTypeId.ROADMAP,
          mapId: 'BIG_G_MAP',
        };
        const map = new google.maps.Map(this.$refs.bigMap, options);

        // Add marker.
        const marker = new AdvancedMarkerElement({
          position: position,
          map: map
        });

        // Add radius.
        if (this.hasRadius) {
          const circle = new google.maps.Circle({
            map: map,
            strokeColor: '#000000',
            strokeOpacity: 0.1,
            strokeWeight: 2,
            fillColor: '#FFFFFF',
            fillOpacity: 0.35,
            center: position,
            radius: Math.round((this.radiusMiles * 1609.34)), // Convert miles to meters.
            editable: false
          });
        }
      },
    },

    // Computed properties.
    computed: {

      /**
       * Trigger map css.
       */
      triggerMapStyle: function () {
        return 'width: ' + this.triggerMapWidth + '; height: ' + this.triggerMapHeight;
      },

      /**
       * Do we have to draw the circle.
       */
      hasRadius: function () {
        return this.showRadius && (this.radiusMiles > 0);
      },

    },

    /**
     * Initialize component on mount.
     */
    async mounted() {
      const google = await gmapsInit(this.apiUrl, this.apiKey);
      const position = new google.maps.LatLng(this.latitude, this.longitude);

      // Init trigger map.
      await this.initTriggerMap(position);
    }
  }
</script>

<style scoped>
  .bigMap {
    width: 100%;
    height: 50vh;
  }
</style>
